import { GLOREELS_API, GLOREELS_ORIGIN, GLOREELS_SSO_ENABLED } from '@/constant/sso';

/**
 * 글로릴스 SSO 클라이언트 — 전부 쿠키 없는(CORS, credentials 생략) 호출이다.
 * 글로릴스 세션은 DB 행이라 쿠키 조작이 필요 없고, 소듀 신원은 Bearer 로 증명한다.
 */

/** 소듀 세션으로 글로릴스 로그인 코드 발급 (소듀 → 글로릴스 방향의 1단계) */
export async function requestGloreelsLoginCode(
    accessToken: string,
    opts: { next?: string; returnTo?: string } = {},
): Promise<{ code: string } | { error: 'email_conflict' | string }> {
    const res = await fetch(GLOREELS_API.fromSodu, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ next: opts.next ?? '/home', return_to: opts.returnTo }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { error: data?.error || `http_${res.status}` };
    return { code: data.code };
}

/**
 * 로그인 즉시 자동 전파(eager) — 소듀 로그인 성공 직후 호출한다.
 *
 * 글로릴스를 스쳐(세션 쿠키만 심고) 지정한 소듀 URL 로 곧장 되돌아온다.
 * 두 서비스가 다른 도메인이라 이 왕복(짧은 화면 전환)이 유일한 방법이다.
 *
 * true 를 반환하면 top-level 이동이 시작된 것 — 호출자는 더 이상 라우팅하지 말 것.
 * 실패(네트워크·429·conflict)하면 false — 전파 없이 소듀만 로그인된 채 진행한다.
 * 재시도하지 않는다: 전파 실패가 로그인을 막아선 안 된다.
 */
export async function eagerLoginToGloreels(accessToken: string, returnTo: string): Promise<boolean> {
    if (!GLOREELS_SSO_ENABLED) return false;
    // 소도몰 계정은 글로릴스 연동 대상이 아니다 — studio 호스트로 들어와도 전파하지 않는다.
    // (신규 탭 첫 로그인처럼 등급이 아직 로드되지 않았으면 통과하지만, 그 경우도
    //  studio 호스트라 return_to 가 화이트리스트에 있어 루프는 생기지 않는다.)
    const { grade_name } = (await import('@/store/modules/spaceUserV2Store')).default.getState();
    if (grade_name === '소도몰') return false;
    try {
        const result = await requestGloreelsLoginCode(accessToken, { returnTo });
        if (!('code' in result)) return false;
        const { GLOREELS_SSO_ENTER } = await import('@/constant/sso');
        window.location.replace(`${GLOREELS_SSO_ENTER}#code=${result.code}`);
        return true;
    } catch {
        return false;
    }
}

/** 글로릴스가 발급한 로그인 코드 교환 → 소듀 자격증명 (글로릴스 → 소듀 방향의 착지) */
export async function redeemGloreelsCode(code: string): Promise<{
    user_id: string;
    user_secret: string;
    redirect_to: string;
} | null> {
    const res = await fetch(GLOREELS_API.redeem, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    if (!res.ok) return null;
    return res.json().catch(() => null);
}

/** 로그아웃 코드 검증 — 검증 없이 로그아웃하면 logout CSRF 가 된다 */
export async function redeemGloreelsLogoutCode(code: string): Promise<boolean> {
    const res = await fetch(GLOREELS_API.redeemLogout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });
    return res.ok;
}

/**
 * 소듀 로그아웃을 글로릴스에 백채널로 전파.
 * ★ signOutSession() "이전에" 호출해야 한다 — 토큰이 지워지면 인증할 수 없다.
 * sendBeacon 은 커스텀 헤더 불가 → 토큰을 바디({t})에 싣는다 (text/plain, preflight 없음).
 */
export function notifyGloreelsLogout(accessToken: string): void {
    if (!GLOREELS_SSO_ENABLED || !accessToken) return;
    try {
        const body = new Blob([JSON.stringify({ t: accessToken })], {
            type: 'text/plain;charset=UTF-8',
        });
        if (navigator.sendBeacon?.(GLOREELS_API.logout, body)) return;
    } catch {
        /* 폴백으로 */
    }
    void fetch(GLOREELS_API.logout, {
        method: 'POST',
        keepalive: true,
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ t: accessToken }),
    }).catch(() => {});
}

/** 소듀 프로필 영역 연동 버튼의 3-상태. none 이면 버튼을 렌더하지 않는다. */
export type GloreelsLinkState = 'linked' | 'canClaim' | 'none';

/**
 * 글로릴스 연동 상태 조회 — 프로필 영역 버튼(바로가기/연동하기) 근거.
 *
 *  - linked   : 이미 연동됨 → "글로릴스 바로가기"
 *  - canClaim : 미연동 + 글로릴스에 같은 이메일 계정 존재 → "글로릴스 연동하기"
 *  - none     : 그 외(연동 불가) → 버튼 없음
 *
 * 킬스위치 OFF·네트워크 실패·미인증은 모두 none 으로 안전 폴백한다
 * (버튼 유무 정도라 실패해도 로그인 흐름에 영향 없음).
 */
export async function fetchGloreelsLinkStatus(accessToken: string): Promise<GloreelsLinkState> {
    if (!GLOREELS_SSO_ENABLED || !accessToken) return 'none';
    try {
        const res = await fetch(GLOREELS_API.status, {
            method: 'GET',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return 'none';
        const data = await res.json().catch(() => ({}));
        if (data?.linked) return 'linked';
        if (data?.can_claim) return 'canClaim';
        return 'none';
    } catch {
        return 'none';
    }
}

/**
 * 글로릴스 연동 물리적 해제 — 편집기 "가져오기" 블록의 [해제] 버튼.
 * 글로릴스의 SoduLink 행을 삭제한다. 성공 시 true.
 */
export async function unlinkGloreels(accessToken: string): Promise<boolean> {
    if (!GLOREELS_SSO_ENABLED || !accessToken) return false;
    try {
        const res = await fetch(GLOREELS_API.unlink, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        return res.ok;
    } catch {
        return false;
    }
}

/** 연동된 글로릴스 계정(연동됨 펼침목록 표시용) */
export interface GloreelsLinkedAccount {
    email: string | null;
    name: string | null;
}

/**
 * 연동 상태 + 연동된 글로릴스 계정을 한 번에 조회 — "글로릴스 연동됨" 펼침목록 근거.
 * fetchGloreelsLinkStatus 와 같은 엔드포인트지만 account 까지 함께 돌려준다.
 */
export async function fetchGloreelsLinkInfo(
    accessToken: string,
): Promise<{ state: GloreelsLinkState; account: GloreelsLinkedAccount | null }> {
    if (!GLOREELS_SSO_ENABLED || !accessToken) return { state: 'none', account: null };
    try {
        const res = await fetch(GLOREELS_API.status, {
            method: 'GET',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return { state: 'none', account: null };
        const data = await res.json().catch(() => ({}));
        const state: GloreelsLinkState = data?.linked ? 'linked' : data?.can_claim ? 'canClaim' : 'none';
        const account =
            state === 'linked' && data?.account
                ? { email: data.account.email ?? null, name: data.account.name ?? null }
                : null;
        return { state, account };
    } catch {
        return { state: 'none', account: null };
    }
}

/**
 * 글로릴스 계정 연동(claim) 팝업 열기 — 버튼·안내 알림이 공용으로 쓴다.
 *
 * 카카오/네이버 소셜 로그인처럼 작은 창을 띄운다. 글로릴스 로그인·연결은 글로릴스
 * 자기 페이지(/sso/link)에서만 이뤄진다(비밀번호를 소듀가 받지 않음). 소듀 토큰은
 * fragment(#st=)로만 실어 로그·Referer 에 남기지 않는다.
 *
 * 팝업이 닫히거나 연결 완료 신호(same-origin postMessage)가 오면 onSettled 를 호출한다
 * — 호출자는 여기서 연동 상태를 다시 조회해 UI 를 갱신하면 된다.
 * 팝업이 차단되면 같은 탭 이동으로 폴백한다.
 */
export function openGloreelsClaimPopup(accessToken: string, onSettled?: () => void): void {
    if (!GLOREELS_SSO_ENABLED || !accessToken) return;
    const url = `${GLOREELS_ORIGIN}/sso/link#st=${encodeURIComponent(accessToken)}`;
    const w = 460;
    const h = 640;
    const y = window.top?.outerHeight
        ? Math.max(0, (window.top.outerHeight - h) / 2 + (window.top.screenY ?? 0))
        : 80;
    const x = window.top?.outerWidth
        ? Math.max(0, (window.top.outerWidth - w) / 2 + (window.top.screenX ?? 0))
        : 80;
    const popup = window.open(
        url,
        'gloreels-link',
        `popup,width=${w},height=${h},left=${Math.round(x)},top=${Math.round(y)}`,
    );
    if (!popup) {
        window.location.href = url;
        return;
    }
    const onMessage = (e: MessageEvent) => {
        if (e.origin !== GLOREELS_ORIGIN) return;
        if ((e.data as { type?: string } | null)?.type === 'sodu-link-done') onSettled?.();
    };
    window.addEventListener('message', onMessage);
    // 팝업이 크로스오리진(글로릴스)을 왕복하면 opener 참조가 끊겨 postMessage·popup.closed 가
    // 불안정하다. 사용자가 팝업을 닫고 이 창으로 "돌아올 때(focus)" 상태를 재확인하는 게 가장 확실하다.
    // onSettled(상태 재조회)는 멱등이라 여러 번 불려도 안전하다.
    const onFocus = () => onSettled?.();
    window.addEventListener('focus', onFocus);
    let ticks = 0;
    const timer = window.setInterval(() => {
        ticks += 1;
        if (popup.closed || ticks > 600) {
            window.clearInterval(timer);
            window.removeEventListener('message', onMessage);
            window.removeEventListener('focus', onFocus);
            onSettled?.();
        }
    }, 700);
}
